import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectLoading } from "modules/notification";
import { generatePaginationOptions, updateUrlQueryParam } from "lib/helper";
import {
  changeBulkSelectedAvailableStockout,
  changeBulkSelectedStockout,
  changeSelectedStockout,
  exportStockout,
  getStockoutList,
  resetStockoutListing,
} from "modules/stockflow/redux";
import moment from "moment";
import StockoutListingComponent from "./stockoutListing.component";
import { useHistory } from "react-router-dom";
import { AccessRights } from "lib/constants/accessRights";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";
import LoadingComponent from "components/loading";
import { unwrapResult } from "@reduxjs/toolkit";
import { getLang } from "app/feature/constants";

function StockoutListingContainer({ type }) {
  const hasStockflowViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.STOCKOUT_READ)
  );
  const params = new URLSearchParams(window.location.search);
  const date = params.get("date");
  const status = params.get("status");
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.constant.languages);
  const language = useSelector((state) => state.profile.language);

  const hasStockflowStockoutViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.STOCKOUT_READ));
  const hasStockflowDealerViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.DEALER_READ));
  const isFetching = useSelector((state) =>
    selectLoading(state, getStockoutList.typePrefix)
  );

  const totalFiltered = useSelector(
    (state) => state.stockflowStockout.stockoutList.totalFiltered
  );
  const totalRecords = useSelector(
    (state) => state.stockflowStockout.stockoutList.totalRecords
  );
  const stockoutList = useSelector(
    (state) => state.stockflowStockout.stockoutList.list
  );
  const hasCustomFields = useSelector(
    (state) => state.stockflowStockout.stockoutList.hasCustomFields
  );

  const customFieldsConfig = useSelector(
    (state) => state.stockflowStockout.stockoutList.customFieldsConfig
  );

  const selectedIds = useSelector(
    (state) => state.stockflowStockout.selectedIds
  );
  const isSelectAll = useSelector(
    (state) => state.stockflowStockout.isSelectAll
  );
  const token = useSelector((state) => state.session.accessToken);
  const isError = useSelector(
    (state) => state.stockflowStockout.isLoadingStockoutListError
  );
  const isSearchResult = stockoutList.length < totalRecords;
  const [paginationOptions, setPaginationOptions] = useState([]);
  // const [isExporting, setIsExporting] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFilterUpdated, setIsFilterUpdated] = useState(false);
  const [tableFilter, setTableFilter] = useState({
    page: 0,
    rowsPerPage: 25,
    order: "desc",
    orderBy: "created_at",
    search: "",
    nodeIds: [],
    branch: [],
    fromDate: moment().subtract(2, "months").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
    stockoutFrom: [],
    stockoutTo: [],
    respondDate: null,
    customFields: [],
    status: null,
  });
  const [isExporting, setIsExporting] = useState(false);

  const statusDropdown = [
    { name: "-", value: "" },
    { name: getLang(lang, "label.PENDING"), value: "pending" },
    { name: getLang(lang, "label.COMPLETED"), value: "completed" },
    { name: getLang(lang, "label.REJECTED"), value: "rejected" },
    { name: getLang(lang, "label.CANCELLED"), value: "cancelled" },
  ];

  const query = new URLSearchParams(window.location.search);
  const tableFilterInitialRef = useRef(true);

  useEffect(() => {
    return () => {
      dispatch(resetStockoutListing());
    };
  }, [dispatch]);

  useEffect(() => {
    const isFilter = query.has("is_filter");
    let filter = sessionStorage.getItem("stockout_filter");

    let temp = { ...tableFilter };
    if (isFilter && filter) {
      temp = JSON.parse(filter);
      setIsFilterUpdated(true);
      setIsFilterOpen(true);
    }

    if (isFilter) {
      updateUrlQueryParam(null, ["is_filter"]);
    }
    sessionStorage.removeItem("stockout_filter");

    if (date) {
      temp = {
        ...temp,
        fromDate: moment(date).format("YYYY-MM-DD"),
        toDate: moment(date).format("YYYY-MM-DD"),
      };
    }

    if (status) {
      let statusObj = statusDropdown.find((s) => s.value === status);
      temp = {
        ...temp,
        status: statusObj,
      };
    }
    setTableFilter(temp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tableFilterInitialRef.current) {
      dispatch(resetStockoutListing());
      handleDataLoad();
    }
    tableFilterInitialRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilter, type]);

  useEffect(() => {
    dispatch(resetStockoutListing());
  }, [dispatch, type]);

  useEffect(() => {
    setPaginationOptions(generatePaginationOptions(totalFiltered));
  }, [totalFiltered]);

  useEffect(() => {
    if (!hasStockflowViewAccessRight) {
      history.push("/admin/dashboard");
    }
  }, [history, hasStockflowViewAccessRight]);

  if (!hasStockflowViewAccessRight) return <LoadingComponent />;

  const handleDataLoad = () => {
    let fields = tableFilter.customFields.reduce((list, c) => {
      if (c.info) {
        list.push({
          uuid: c.info.uuid,
          search: c.search,
        });
      }
      return list;
    }, []);

    let payload = {
      length: tableFilter.rowsPerPage,
      start: tableFilter.rowsPerPage * tableFilter.page,
      order: tableFilter.order,
      order_by: tableFilter.orderBy,
      status: tableFilter.status ? tableFilter.status.value : null,
      filter: {
        search: tableFilter.search,
        nodes: tableFilter.nodeIds,
        from_date: tableFilter.fromDate,
        to_date: tableFilter.toDate,
        stockout_from: tableFilter.stockoutFrom,
        stockout_to: tableFilter.stockoutTo,
        respond_date: tableFilter.respondDate,
      },
      custom_fields: fields,
    };
    dispatch(getStockoutList(payload));
  };

  const handleChangePage = (event, newPage) => {
    let newData = {
      ...tableFilter,
      page: newPage,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleChangeRowsPerPage = (event) => {
    let newData = {
      ...tableFilter,
      page: 0,
      rowsPerPage: event.target.value,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleSearch = (filter) => {
    let nodeIds =
      filter.branch.length > 0 ? filter.branch.map((br) => br.node_id) : [];

    let newData = {
      ...tableFilter,
      page: 0,
      search: filter.search,
      fromDate: filter.dateRange?.from
        ? moment(filter.dateRange.from).format("YYYY-MM-DD")
        : "",
      toDate: filter.dateRange?.to
        ? moment(filter.dateRange.to).format("YYYY-MM-DD")
        : "",
      nodeIds: nodeIds,
      branch: filter.branch,
      respondDate: filter.respondDate,
      stockoutFrom: filter.stockoutFrom,
      stockoutTo: filter.stockoutTo,
      customFields: filter.customFields,
      status: filter.status,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const clearSearch = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      search: "",
      nodeIds: [],
      branch: [],
      fromDate: moment().subtract(2, "months").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      stockoutFrom: [],
      stockoutTo: [],
      respondDate: null,
      customFields: [],
      status: null,
    };
    setIsFilterUpdated(false);
    setTableFilter(newData);
  };

  const handleSelectStockout = (index, isSelected) => {
    dispatch(changeSelectedStockout({ index, isSelected }));
  };

  const handleSelectAvailable = () => {
    dispatch(changeBulkSelectedAvailableStockout({ value: true }));
  };

  const handleSelectAll = () => {
    dispatch(changeBulkSelectedStockout({ value: true }));
  };

  const handleDeselectAll = () => {
    dispatch(changeBulkSelectedStockout({ value: false }));
  };

  const changeOrder = (orderBy, order) => {
    let newData = {
      ...tableFilter,
      order,
      orderBy,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const resetFilter = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      nodeIds: [],
      branch: [],
      respondDate: "",
      stockoutFrom: [],
      stockoutTo: [],
      customFields: [],
      status: null,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleViewDetail = (url) => {
    if (isFilterUpdated) {
      updateUrlQueryParam({ is_filter: true });
      sessionStorage.setItem("stockout_filter", JSON.stringify(tableFilter));
    }
    history.push(url);
  };

  const handleExport = (fileType) => {
    setIsExporting(true);

    let fields = tableFilter.customFields.reduce((list, c) => {
      if (c.info) {
        list.push({
          uuid: c.info.uuid,
          search: c.search,
        });
      }
      return list;
    }, []);

    const info = {
      token: token,
      search: tableFilter.search,
      nodeIds: tableFilter.nodeIds,
      fromDate: tableFilter.fromDate,
      toDate: tableFilter.toDate,
      stockoutFrom: tableFilter.stockoutFrom,
      stockoutTo: tableFilter.stockoutTo,
      respondDateFrom: tableFilter.respondDate
        ? tableFilter.respondDate.from
        : null,
      respondDateTo: tableFilter.respondDate
        ? tableFilter.respondDate.to
        : null,
      customFields: fields,
      order: tableFilter.order,
      orderBy: tableFilter.orderBy,
      status: tableFilter.status ? tableFilter.status.value : null,
      fileType: fileType,
    };
    dispatch(exportStockout(info))
      .then(unwrapResult)
      .finally(() => {
        setIsExporting(false);
      });
  };

  return (
    <>
      <StockoutListingComponent
        hasStockflowStockoutViewAccessRight={
          hasStockflowStockoutViewAccessRight
        }
        hasStockflowDealerViewAccessRight={hasStockflowDealerViewAccessRight}
        type={type}
        stockouts={stockoutList}
        isFetching={isFetching}
        selectedIds={selectedIds}
        isSelectAll={isSelectAll}
        selectedCount={
          !isSelectAll ? selectedIds.length : totalFiltered - selectedIds.length
        }
        isError={isError}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={tableFilter.page}
        rowsPerPage={tableFilter.rowsPerPage}
        totalFiltered={totalFiltered}
        handleSearch={handleSearch}
        language={language}
        clearSearch={clearSearch}
        handleReload={handleDataLoad}
        isSearchResult={isSearchResult}
        handleSelectStockout={handleSelectStockout}
        handleSelectAvailable={handleSelectAvailable}
        handleSelectAll={handleSelectAll}
        handleDeselectAll={handleDeselectAll}
        paginationOptions={paginationOptions}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        tableFilter={tableFilter}
        changeOrder={changeOrder}
        lang={lang}
        resetFilter={resetFilter}
        handleViewDetail={handleViewDetail}
        hasCustomFields={hasCustomFields}
        customFieldsConfig={customFieldsConfig}
        isExporting={isExporting}
        handleExport={handleExport}
        statusDropdown={statusDropdown}
      />
    </>
  );
}

export default StockoutListingContainer;
